import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface NeonBar {
  position: [number, number, number];
  rotation?: [number, number, number];
  length: number;
  thickness?: number;
}

interface AnimatedNeonEffectProps {
  bars: NeonBar[];
  blinkSpeed?: number;
  colorChangeSpeed?: number;
  intensity?: number;
  onBlink?: () => void;
}

export function AnimatedNeonEffect({
  bars,
  blinkSpeed = 2,
  colorChangeSpeed = 0.5,
  intensity = 1,
  onBlink,
}: AnimatedNeonEffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  const materialsRef = useRef<THREE.MeshBasicMaterial[]>([]);
  const lastSoundTime = useRef(0);
  const nextSoundDelay = useRef(1.5 + Math.random() * 2);

  // Shared geometry - low poly
  const tubeGeo = useMemo(() => new THREE.CylinderGeometry(1, 1, 1, 6), []);

  // Initialize materials
  useMemo(() => {
    materialsRef.current = bars.map(() =>
      new THREE.MeshBasicMaterial({
        color: new THREE.Color('#00BFFF'),
        transparent: true,
        opacity: 1,
      })
    );
  }, [bars.length]);

  // Animation loop - simplified
  useFrame((state) => {
    const time = state.clock.elapsedTime;

    bars.forEach((_, idx) => {
      const material = materialsRef.current[idx];
      if (!material) return;

      // Color oscillation between blue and red
      const colorPhase = Math.sin(time * colorChangeSpeed + idx * 0.5) * 0.5 + 0.5;
      const blueColor = new THREE.Color('#00BFFF');
      const redColor = new THREE.Color('#FF1744');
      const currentColor = blueColor.clone().lerp(redColor, colorPhase);

      // Simplified blink effect
      const blinkBase = Math.sin(time * blinkSpeed * 3 + idx * 1.7);
      const blinkValue = Math.max(0.4, blinkBase * 0.3 + 0.7);

      material.color = currentColor;
      material.opacity = blinkValue * intensity;
    });

    // Play neon sound at random intervals (1.5-3.5 seconds)
    if (onBlink && time - lastSoundTime.current > nextSoundDelay.current) {
      onBlink();
      lastSoundTime.current = time;
      nextSoundDelay.current = 1.5 + Math.random() * 2;
    }
  });

  return (
    <group ref={groupRef}>
      {bars.map((bar, idx) => {
        const thickness = bar.thickness || 0.08;

        return (
          <mesh
            key={`neon-bar-${idx}`}
            position={bar.position}
            rotation={bar.rotation ? bar.rotation.map(r => r) as [number, number, number] : [0, 0, 0]}
            scale={[thickness, bar.length, thickness]}
            geometry={tubeGeo}
          >
            <primitive object={materialsRef.current[idx] || new THREE.MeshBasicMaterial()} attach="material" />
          </mesh>
        );
      })}
      {/* Single ambient glow instead of per-bar lights */}
      <pointLight color="#8844AA" intensity={3} distance={15} decay={2} position={[0, 3, 0]} />
    </group>
  );
}

// Simplified neon text - just a glowing plane with text effect
interface UpsideDownNeonTextProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  blinkSpeed?: number;
}

export function UpsideDownNeonText({
  position,
  rotation = [0, 0, 0],
  scale = 1,
  blinkSpeed = 1.5,
}: UpsideDownNeonTextProps) {
  const materialRef = useRef<THREE.MeshBasicMaterial>(null);

  // Animation
  useFrame((state) => {
    if (!materialRef.current) return;
    const time = state.clock.elapsedTime;

    // Color oscillation - mostly red with blue flashes
    const colorPhase = Math.sin(time * 0.8) * 0.5 + 0.5;
    const blueColor = new THREE.Color('#00BFFF');
    const redColor = new THREE.Color('#FF1744');
    const currentColor = redColor.clone().lerp(blueColor, colorPhase * 0.3);

    // Blink effect
    const blink = Math.sin(time * blinkSpeed * 2);
    const blinkValue = Math.max(0.5, blink * 0.25 + 0.75);

    materialRef.current.color = currentColor;
    materialRef.current.opacity = blinkValue;
  });

  // Simplified: just render a few key shapes to suggest "UPSIDE DOWN"
  const letterWidth = 0.35 * scale;
  const letterHeight = 0.5 * scale;
  const thickness = 0.025 * scale;
  const spacing = 0.45 * scale;

  // Very simplified letter representations - just vertical and horizontal bars
  const simplifiedLetters = useMemo(() => {
    const letters: { x: number; y: number; w: number; h: number }[] = [];
    let x = 0;

    // U - two verticals + bottom
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x: x + letterWidth - thickness, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: 0, w: letterWidth, h: thickness });
    x += spacing;

    // P - vertical + top box
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: letterHeight - thickness, w: letterWidth * 0.7, h: thickness });
    letters.push({ x, y: letterHeight * 0.5, w: letterWidth * 0.7, h: thickness });
    x += spacing;

    // S - three horizontals
    letters.push({ x, y: letterHeight - thickness, w: letterWidth, h: thickness });
    letters.push({ x, y: letterHeight * 0.5 - thickness / 2, w: letterWidth, h: thickness });
    letters.push({ x, y: 0, w: letterWidth, h: thickness });
    x += spacing;

    // I - vertical
    letters.push({ x: x + letterWidth * 0.4, y: 0, w: thickness, h: letterHeight });
    x += spacing * 0.6;

    // D - vertical + curves
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: letterHeight - thickness, w: letterWidth * 0.6, h: thickness });
    letters.push({ x, y: 0, w: letterWidth * 0.6, h: thickness });
    x += spacing;

    // E - vertical + three horizontals
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: letterHeight - thickness, w: letterWidth * 0.8, h: thickness });
    letters.push({ x, y: letterHeight * 0.5 - thickness / 2, w: letterWidth * 0.6, h: thickness });
    letters.push({ x, y: 0, w: letterWidth * 0.8, h: thickness });
    x += spacing * 1.3;

    // D
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: letterHeight - thickness, w: letterWidth * 0.6, h: thickness });
    letters.push({ x, y: 0, w: letterWidth * 0.6, h: thickness });
    x += spacing;

    // O - box
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x: x + letterWidth - thickness, y: 0, w: thickness, h: letterHeight });
    letters.push({ x, y: letterHeight - thickness, w: letterWidth, h: thickness });
    letters.push({ x, y: 0, w: letterWidth, h: thickness });
    x += spacing;

    // W - two Vs
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x: x + letterWidth * 0.5 - thickness / 2, y: 0, w: thickness, h: letterHeight * 0.5 });
    letters.push({ x: x + letterWidth - thickness, y: 0, w: thickness, h: letterHeight });
    x += spacing;

    // N - two verticals + diagonal
    letters.push({ x, y: 0, w: thickness, h: letterHeight });
    letters.push({ x: x + letterWidth - thickness, y: 0, w: thickness, h: letterHeight });
    x += spacing;

    return { letters, totalWidth: x };
  }, [scale, letterWidth, letterHeight, thickness, spacing]);

  return (
    <group position={position} rotation={rotation}>
      <group position={[-simplifiedLetters.totalWidth / 2, -letterHeight / 2, 0]}>
        {simplifiedLetters.letters.map((l, idx) => (
          <mesh key={`letter-${idx}`} position={[l.x + l.w / 2, l.y + l.h / 2, 0]}>
            <boxGeometry args={[l.w, l.h, thickness]} />
            <meshBasicMaterial
              ref={idx === 0 ? materialRef : undefined}
              color="#FF1744"
              transparent
              opacity={0.9}
            />
          </mesh>
        ))}
      </group>
      {/* Single light for glow */}
      <pointLight color="#FF2222" intensity={5} distance={10} decay={2} />
    </group>
  );
}
